import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin, Clock, Instagram, Check } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Błąd. Spróbuj ponownie.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* Hero */}
      <section data-testid="contact-hero" className="relative min-h-[40vh] flex items-end pt-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ background: "radial-gradient(circle at 50% 40%, rgba(59,91,255,0.08) 0%, transparent 60%)" }} className="absolute inset-0" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-12 w-full relative">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Napisz do mnie</p>
            <h1 className="font-anton text-6xl sm:text-8xl text-white uppercase leading-none">KONTAKT</h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact form */}
          <FadeUp>
            <h2 className="font-anton text-3xl text-white uppercase mb-8">WYŚLIJ WIADOMOŚĆ</h2>

            {sent ? (
              <div data-testid="contact-success" className="border border-violet-600/30 p-10 text-center" style={{ background: "rgba(139,0,255,0.05)" }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: "rgba(139,0,255,0.15)", border: "1px solid rgba(139,0,255,0.3)" }}>
                  <Check size={28} className="text-violet-500" />
                </div>
                <h3 className="font-anton text-2xl text-white uppercase mb-2">Wiadomość Wysłana!</h3>
                <p className="text-white/60 text-sm">Odezwę się jak najszybciej. Zazwyczaj odpowiadam w ciągu 24h.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-7">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">Imię i nazwisko *</label>
                    <input data-testid="contact-name" type="text" required value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="input-dark" placeholder="Anna Kowalska" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">E-mail *</label>
                    <input data-testid="contact-email" type="email" required value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="input-dark" placeholder="anna@example.com" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">Telefon</label>
                    <input data-testid="contact-phone" type="tel" value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="input-dark" placeholder="+48 000 000 000" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">Temat</label>
                    <select data-testid="contact-subject" value={form.subject}
                      onChange={e => setForm({ ...form, subject: e.target.value })}
                      className="input-dark bg-transparent" style={{ appearance: "none" }}>
                      <option value="">Wybierz temat</option>
                      <option value="wizyta">Rezerwacja wizyty</option>
                      <option value="szkolenie">Zapytanie o szkolenie</option>
                      <option value="wspolpraca">Współpraca</option>
                      <option value="inne">Inne</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-white/40 block mb-2">Wiadomość *</label>
                  <textarea data-testid="contact-message" required rows={6} value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    className="input-dark resize-none" placeholder="W czym mogę Ci pomóc?" />
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button data-testid="contact-submit" type="submit" disabled={loading}
                  className="btn-primary disabled:opacity-50">
                  {loading ? "Wysyłanie..." : "Wyślij Wiadomość"} <ArrowRight size={14} />
                </button>
              </form>
            )}
          </FadeUp>

          {/* Info */}
          <FadeUp delay={0.2}>
            <h2 className="font-anton text-3xl text-white uppercase mb-8">INFORMACJE</h2>
            <div className="space-y-8 mb-12">
              {[
                { icon: <Phone size={18} />, label: "Telefon", val: "+48 798 943 906", href: "tel:+48798943906" },
                { icon: <Mail size={18} />, label: "E-mail", val: "fryzjerartystamilena@gmail.com", href: "mailto:fryzjerartystamilena@gmail.com" },
                { icon: <Instagram size={18} />, label: "Instagram", val: "@fryzjer_artysta", href: "https://www.instagram.com/fryzjer_artysta/" },
                { icon: <MapPin size={18} />, label: "Adres", val: "Tarnów, ul. Reymonta 25\n33-100 Małopolska", href: null },
                { icon: <Clock size={18} />, label: "Godziny", val: "Pon – Sob: 8:00 – 19:00\nNiedziela: zamknięte", href: null },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-5">
                  <div className="text-violet-500 border border-violet-600/30 p-3 flex-shrink-0">{c.icon}</div>
                  <div>
                    <div className="text-white/30 text-xs uppercase tracking-[0.2em] mb-1">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                        data-testid={`contact-link-${i}`}
                        className="text-white text-sm hover:text-violet-400 transition-colors whitespace-pre-line">{c.val}</a>
                    ) : (
                      <span className="text-white text-sm whitespace-pre-line">{c.val}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map placeholder */}
            <div data-testid="map-placeholder" className="border border-white/8 overflow-hidden aspect-video relative">
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: "#0A0A0A" }}>
                <MapPin size={32} className="text-violet-500/40 mb-3" />
                <p className="text-white/20 text-sm uppercase tracking-[0.2em]">Mapa - Tarnów</p>
                <p className="text-white/15 text-xs mt-1">Dokładny adres po rezerwacji</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>

      <Footer />
    </div>
  );
}
